export interface CategoryItem {
  id?: string | number;
  name: string;
  text?: string;
  category?: string;
  title?: string;
  status?: boolean;
  icon?: string;
  slug?: string;
  [key: string]: unknown;
}

export const getCategoryDisplayName = (item?: CategoryItem | null): string => {
  if (!item) return "";
  return (
    item.text ||
    item.name ||
    item.category ||
    item.title ||
    ""
  ).trim();
};

export const normalizeCategories = (
  rawList: CategoryItem[]
): CategoryItem[] => {
  return rawList
    .map((item, index) => {
      const displayName = getCategoryDisplayName(item);
      return {
        ...item,
        id: item?.id ?? index + 1,
        name: displayName,
      };
    })
    .filter((item) => !!item.name && item.status !== false);
};

export const extractCategoriesFromResponse = (response: unknown): CategoryItem[] => {
  const data = (response as { data?: unknown })?.data;
  const rawList = Array.isArray(data)
    ? data
    : Array.isArray((data as { data?: unknown })?.data)
      ? ((data as { data: CategoryItem[] }).data)
      : [];

  return normalizeCategories(rawList as CategoryItem[]);
};

export const extractLastSubmittedCategory = (
  response: unknown
): CategoryItem | null => {
  const data = (response as { data?: unknown })?.data;
  if (!data) return null;

  // Supports: { data: category }, { data: { data: category } }, { data: { category: ... } }
  const candidate =
    (data as CategoryItem)?.id ||
    (data as CategoryItem)?.text ||
    (data as CategoryItem)?.name ||
    (data as CategoryItem)?.category
      ? (data as CategoryItem)
      : (data as { data?: CategoryItem })?.data ||
        (data as { category?: CategoryItem })?.category ||
        (data as { lastSubmitted?: CategoryItem })?.lastSubmitted ||
        (data as { last_submitted?: CategoryItem })?.last_submitted ||
        null;

  if (!candidate || typeof candidate !== "object") return null;

  const name = getCategoryDisplayName(candidate);
  if (!name && candidate.id == null) return null;

  return {
    ...candidate,
    name: name || String(candidate.id),
  };
};

/**
 * Pick the next category after the last submitted one, wrapping to the first.
 * If nothing was submitted yet (or last is unknown), use the first in list order.
 */
export const getNextCategoryInSequence = (
  categories: CategoryItem[],
  lastSubmitted: CategoryItem | null
): CategoryItem | null => {
  if (!categories.length) return null;
  if (!lastSubmitted) return categories[0];

  const lastId = lastSubmitted.id;
  const lastName = getCategoryDisplayName(lastSubmitted).toLowerCase();

  const lastIndex = categories.findIndex((item) => {
    if (lastId != null && item.id != null && String(item.id) === String(lastId)) {
      return true;
    }
    return getCategoryDisplayName(item).toLowerCase() === lastName;
  });

  if (lastIndex === -1) return categories[0];
  return categories[(lastIndex + 1) % categories.length];
};
